import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MkdfLogo from './MkdfLogo';
import DFFilesLogo from './DFFilesLogo';
import MessageDialog from './MessageDialog';
import { useMessageDialog } from '../hooks/useMessageDialog';
import { useAnnouncementsOptional } from '../contexts/AnnouncementContext';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { dialogState, closeDialog, showConfirm } = useMessageDialog();
  const { unreadCount } = useAnnouncementsOptional();

  const handleLogout = () => {
    showConfirm(
      'ログアウト',
      'ログアウトしてもよろしいですか？',
      () => {
        closeDialog();
        logout();
        navigate('/login');
      },
      { confirmLabel: 'ログアウト' }
    );
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const currentPath = window.location.pathname;

  const navItems = [
    { path: '/files', label: 'ファイル一覧' },
    { path: '/upload', label: 'アップロード' },
    { path: '/contacts', label: '連絡先' },
    { path: '/received', label: '受信ファイル' },
    { path: '/announcements', label: 'お知らせ' },
    { path: '/settings', label: '設定' },
  ];

  return (
    <>
      <header className="header">
        <div className="header__inner">
          <div className="flex items-center gap-6">
            <div className="header__brand" onClick={() => navigate('/files')}>
              <MkdfLogo height={20} />
              <span className="header__brand-divider">|</span>
              <DFFilesLogo size={18} variant="light" />
            </div>

            <nav className="header__nav">
              {navItems.map(({ path, label }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className={`header__nav-btn ${currentPath === path ? 'header__nav-btn--active' : ''}`}
                >
                  {label}
                  {path === '/announcements' && unreadCount > 0 && (
                    <span className="nav-unread-badge" aria-label={`未読${unreadCount}件`}>
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="header__actions">
            <a
              href="/docs/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--sm btn--secondary"
              title="操作マニュアル"
            >
              <span className="material-symbols-outlined icon-sm">help_outline</span> マニュアル
            </a>

            <span className="header__user-email">{user?.email}</span>

            {user?.role === 'admin' && (
              <button onClick={handleAdminClick} className="btn btn--sm btn--navy">
                <span className="material-symbols-outlined icon-sm">settings</span> 管理画面
              </button>
            )}

            <button onClick={handleLogout} className="btn btn--sm btn--secondary">
              ログアウト
            </button>

            <button
              className="header__menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="メニュー"
            >
              <span className="material-symbols-outlined">{mobileOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className={`header__mobile-nav ${mobileOpen ? 'header__mobile-nav--open' : ''}`}>
        {navItems.map(({ path, label }) => (
          <button
            key={path}
            onClick={() => { navigate(path); setMobileOpen(false); }}
            className={`header__nav-btn ${currentPath === path ? 'header__nav-btn--active' : ''}`}
          >
            {label}
            {path === '/announcements' && unreadCount > 0 && (
              <span className="nav-unread-badge" aria-label={`未読${unreadCount}件`}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
        <a
          href="/docs/index.html"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn--sm btn--secondary w-full"
          onClick={() => setMobileOpen(false)}
        >
          <span className="material-symbols-outlined icon-sm">help_outline</span> マニュアル
        </a>
        <span className="text-muted text-sm" style={{ padding: '8px 16px' }}>{user?.email}</span>
        {user?.role === 'admin' && (
          <button onClick={() => { handleAdminClick(); setMobileOpen(false); }} className="btn btn--sm btn--navy w-full">
            <span className="material-symbols-outlined icon-sm">settings</span> 管理画面
          </button>
        )}
        <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="btn btn--sm btn--secondary w-full">
          ログアウト
        </button>
      </div>

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </>
  );
};

export default Header;
